import axios from "axios";
import { getToken } from "./authApi";
import {ApiResponse, BaseCreateResponse, BaseInfoList, BaseResponse, BaseResponseWithData, baseUrl, deleteWithAuth, GetParams, getWithAuth, getWithAuthAndParams, Guid, ListRespone, PaginationListRespone, patchWithAuth, postWithAuth} from "./baseApi";
import { CampaingListModel } from "./campaingsApi";
import { useProgressStore } from "../hooks/useProgress";

export enum MediaType {
    Video = 0,
    Image = 1,
    VastUrl = 2,
    Youtube = 3,
    YoutubeLive = 4,
    WebPage = 6,
    HiveStack = 7
}

export enum MediaContentType {
    Ads = 0,
    ClientContent = 1,
    SelfContent = 2,
    Backup = 3
}

export enum WebPageType {
    NothingSpecial = 0,
    URL = 1,
    ZIP = 2,
}

export const mediaContentTypeOptions : MediaContentTypeOption[] = [
    {type: MediaContentType.Ads, name: "Campaigns"},
    {type: MediaContentType.ClientContent, name:"Network Content"},
    {type: MediaContentType.SelfContent, name: "Inhouse Content"},
    {type: MediaContentType.Backup, name: "Others"},
]

export type MediaContentTypeOption = {
    type: MediaContentType,
    name: string
}

export type MediaCategory = {
    id: number,
    name: string
}

export type MediaAnalyzeProfile = {
    requestedTime: string,
    lastUpdateTime: string,
    completeTime: string,
    status: MediaAnalyzeStatus,
    mediaInfoContainerKind: string,
    mediaInfoContainerDuration: number,
    mediaInfoContainerBitrate: number,
    mediaInfoContainerSize: number,
    audioCodec: string,
    audioChannels: number,
    audioChannelOrder: string,
    audioSampleRate: number,
    audioDuration: number,
    audioBitrate: number,
    videoCodec: string,
    videoFrameRate: number,
    videoHeight: number,
    videoWidth: number,
    videoDuration: number,
    videoBitrate: number,
    loudnessMeasured: number,
    loudnessRange: number,
    loudnessGatingMode: string,
    loudnessSamplePeak: number,
    loudnessTruePeak: number,
    jobId: string,
    dolbyOutputPath: string,
}

export type MediaTranscode = {
    profileId: Guid,
    requestedTime: string,
    lastUpdateTime: string,
    completeTime: string,
    status: MediaAnalyzeStatus,
    profileName: string,
    canBeRequested: boolean,
    downloadLink: string
}

export enum MediaAnalyzeStatus {
    Requested = 0,
    Posted = 1,
    Pending = 2,
    Running = 3,
    Success = 4,
    Failed = 5,
    Cancelled = 6,
    InternalError = 7,
    Complete = 8
}

export type MediaModel = {
    id: string,
    name : string,
    mediaType: MediaType,
    uploadTime : string,
    sizeMb : number,
    // playouts: number,
    previewLink: string,
    campaigns: CampaingListModel[]
    durationMs: number,
    webVideoLink: string,
    isDeleted: boolean,
    url: string,
    vastUrl:string,
    contentType: MediaContentType,
    transcodeProfile: string,
    clockId: string,
    webPageIsDownloadLocaly: boolean,
    webPageType: WebPageType,
    taxonomiesCategoryId: number | null
}

export type MediaAwaitingApprovalModel = {
    id: Guid,
    name: string,
    mediaType: MediaType,
    durationMs: number,
    previewUrl: string,
    campaigns: string[],
    flightDates: MediaAwaitingApprovalFlightDate[],
    requestDate: string
}

export type MediaAwaitingApprovalFlightDate = {
    id: Guid,
    name: string,
    start: string,
    end: string,
    startTime: string,
    endTime: string
}

export type ApproveExternalOrganisationMediaRequest = {
    mediaId: Guid,
    isApproved: boolean
}

export type YoutubeMediaRequest = {
    name : string,
    url: string,
    isLive: boolean,
    duration: number,
    taxonomiesCategoryId:number | null
}

export type MediaListResponse = {
    medias : MediaModel[]
}

export type MediaVastResponse = {

}

export type MediaVastRequest = {
    name : string,
    url: string,
    contentType: MediaContentType | null,
    clockID: string,
    taxonomiesCategoryId:number | null
}

export type UpdateMediaRequest = {
    newName : string,
    newDurationMs?: number,
    contentType: MediaContentType | null,
    clockID: string,
    taxonomiesCategoryId:number | null
}

export type UpdateMediaResponse = {

}

export type WebPageRequest = {
    name : string,
    url: string,
    contentType: MediaContentType,
    durationSeconds: number,
    clockID: string,
    zipFile: File | null,
    isLocal: boolean,
    webPageType: WebPageType,
    taxonomiesCategoryId:number | null
}

export type HiveStackRequest = {
    name : string,
    contentType: MediaContentType,
    clockID: string,
    taxonomiesCategoryId:number | null
}

export type MediaPlayout = {
    mediaId: Guid,
    count: number,
    mediaName: string
}

export type MediaPlayoutRequest = {
    ids: Guid[],
    playerId: Guid | null
}

export type MediaPaginationRequest = {
    campaignId: Guid[],
    searchString: string,
    startTime: string | null,
    endTime: string | null,
    itemsPerPage: number,
    pageNumber: number
}

export const getMedia = async() : Promise<ApiResponse<MediaListResponse>> => {
    return await getWithAuth<MediaListResponse>({url : `${baseUrl}media`});
}

export const getMediaBaseInfo = async() : Promise<ApiResponse<BaseInfoList>> => {
    return await getWithAuth<BaseInfoList>({url : `${baseUrl}media/base-info`});
}

export const getMediaAwaitingApproval = async() : Promise<ApiResponse<ListRespone<MediaAwaitingApprovalModel>>> => {
    return await getWithAuth<ListRespone<MediaAwaitingApprovalModel>>({url : `${baseUrl}media/external-organisation/not-approved`});
}

export const approveExternalOrganisationMedia = async(request: ApproveExternalOrganisationMediaRequest) : Promise<ApiResponse<BaseResponse>> => {
    let response = await postWithAuth<ApproveExternalOrganisationMediaRequest, BaseResponse>(`${baseUrl}media/external-organisation/approve`, request);
    return response;
}

export const getMediaPlayout = async (request: MediaPlayoutRequest): Promise<ApiResponse<ListRespone<MediaPlayout>>> => {
    return await postWithAuth<MediaPlayoutRequest, ListRespone<MediaPlayout>>(`${baseUrl}media/playouts`, request);
}

export const getMediaWithPagination = async (request: MediaPaginationRequest): Promise<ApiResponse<PaginationListRespone<MediaModel>>> => {
    return await postWithAuth<MediaPaginationRequest, PaginationListRespone<MediaModel>>(`${baseUrl}media/search`, request);
}

export const getMediaById = async(id : Guid) : Promise<ApiResponse<BaseResponseWithData<MediaModel>>> => {
    return await getWithAuth({url : `${baseUrl}media/${id}`});
}

export const uploadMedia = async (formData: any) : Promise<string> => {
    await axios.post(`${baseUrl}media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization' : getToken()
        }
    });

    return "ok"
}

export const uploadMultipleMedia = async (formData: any, setCurrentProgress?:(progress: number)=>void) : Promise<"ok" | "error"> => {
    try {
        await axios.post(`${baseUrl}media/upload`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization' : getToken()
            }, 
            onUploadProgress: (progressEvent:any) => {
                let progress = Math.round(
                    (progressEvent.loaded / progressEvent.total) * 100
                );
                setCurrentProgress && setCurrentProgress(progress);
            }
        });
    } catch (error) {
        setCurrentProgress && setCurrentProgress(0);
    }

    return "ok"
}

export const postMediaVast = async (request: MediaVastRequest) :Promise<ApiResponse<MediaVastResponse>> => {
    return await postWithAuth<MediaVastRequest, MediaVastResponse>(`${baseUrl}media/vast`, request);
}

export const updateMedia = async (id: Guid | undefined, request: UpdateMediaRequest) :Promise<ApiResponse<UpdateMediaResponse>> => {
    return await patchWithAuth<UpdateMediaRequest, UpdateMediaResponse>(`${baseUrl}media/${id}`, request);
}

export const updateMediaVast = async (id: Guid, request: MediaVastRequest) :Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<MediaVastRequest, BaseResponse>(`${baseUrl}media/vast/${id}`, request);
}

export const deleteMedia = async (id: Guid) :Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}media`, id);
}

export const postYoutubeMedia = async (request: YoutubeMediaRequest) :Promise<ApiResponse<{}>> => {
    return await postWithAuth<YoutubeMediaRequest, {}>(`${baseUrl}media/youtube`, request);
}

export const getMediaAnalyzeProfile = async (id:Guid) : Promise<ApiResponse<BaseResponseWithData<MediaAnalyzeProfile>>> => {
    return await getWithAuth<BaseResponseWithData<MediaAnalyzeProfile>>({url : `${baseUrl}media/${id}/analyze`});
}

export const getMediaTranscodes = async (id:Guid) : Promise<ApiResponse<ListRespone<MediaTranscode>>> => {
    return await getWithAuth<ListRespone<MediaTranscode>>({url : `${baseUrl}media/${id}/transcodes`});
}

export const postMediaTranscodeRequest = async (id:Guid, profileId:Guid) :Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<{}, BaseResponse>(`${baseUrl}media/${id}/transcodes/${profileId}`, {});
}

export const getIsUseOriginal = async (id:Guid) : Promise<ApiResponse<BaseResponseWithData<boolean>>> => {
    return await getWithAuth<BaseResponseWithData<boolean>>({url : `${baseUrl}media/${id}/isUseOriginal`});
}

export const postUseOriginal = async (id:Guid) :Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<{}, BaseResponse>(`${baseUrl}media/${id}/userOriginal`, {});
}

export const postUseTranscode = async (id:Guid) :Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<{}, BaseResponse>(`${baseUrl}media/${id}/useTranscoded`, {});
}

export const postWebPage = async (request: WebPageRequest) :Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<WebPageRequest, BaseResponse>(`${baseUrl}media/web`, request);
}

export const postWebPageWithZip = async (request: WebPageRequest) : Promise<ApiResponse<BaseCreateResponse>> => {
    let formData = new FormData();

    formData.append("Url", request.url)
    formData.append("Name", request.name)
    formData.append("ContentType", request.contentType.toString())
    formData.append("DurationSeconds", request.durationSeconds.toString())
    formData.append("ClockId", request.clockID)
    formData.append("IsLocal", request.isLocal.toString())
    formData.append("WebPageType", request.webPageType.toString())
    if (request.taxonomiesCategoryId != null) {
        formData.append("TaxonomiesCategoryId", request.taxonomiesCategoryId.toString());
    }

    if(request.zipFile){
        formData.append("ZipFile", request.zipFile);
    }

    try {
        let result = await axios.post(`${baseUrl}media/web`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization' : getToken()
            }
        });

        return { type:"succes", data: result.data };

    } catch (error: any) {
        if(error.response.status == 401) {
            return { type : "unathorized"}
        }
        return  { type : "unhadledError"}
    }

}

export const updateWebPage = async (id: Guid, request: WebPageRequest) :Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<WebPageRequest, BaseResponse>(`${baseUrl}media/web/${id}`, request);
}

export const postHiveStack = async (request: HiveStackRequest) :Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<HiveStackRequest, BaseResponse>(`${baseUrl}media/hivestack`, request);
}

export const updateHiveStack = async (id: Guid, request: HiveStackRequest) :Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<HiveStackRequest, BaseResponse>(`${baseUrl}media/hivestack/${id}`, request);
}


export const getMediaCategories = async() : Promise<ApiResponse<ListRespone<MediaCategory>>> => {
    return await getWithAuth<ListRespone<MediaCategory>>({url : `${baseUrl}media/taxonomie-categories`});
}
